import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const OutlookSetupGuide = () => {
  const [copiedText, setCopiedText] = React.useState("");

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(""), 2000);
  };

  return (
    <div className="min-h-screen bg-[#F0F6FF] p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/email-outreach"
            className="flex items-center gap-2 text-[#5E78F4] hover:text-[#F14CE2] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-[600] text-[16px]">Back to Dashboard</span>
          </Link>
        </div>

        <div className="bg-white rounded-[20px] shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0078D4] to-[#106EBE] text-white p-8">
            <h1 className="font-[700] text-[32px] mb-4">
              Outlook Integration Setup Guide
            </h1>
            <p className="font-[400] text-[18px] opacity-90">
              Connect your Outlook account to enable sending and receiving
              emails through Microsoft Graph API
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Overview */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-4">
                Overview
              </h2>
              <div className="bg-[#F0F6FF] rounded-[15px] p-6 border border-[#E1E7FF]">
                <p className="font-[400] text-[16px] text-[#2A2A2A] mb-4">
                  To connect your Outlook account, we'll use Microsoft Azure's
                  OAuth 2.0 system. This secure process gives our application
                  permission to send and receive emails on your behalf without
                  storing your password.
                </p>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#3BB82A] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-[600] text-[16px] text-[#2A2A2A] mb-1">
                      What you'll need:
                    </p>
                    <ul className="font-[400] text-[14px] text-[#6B7280] space-y-1">
                      <li>• A Microsoft 365 or Outlook.com email account</li>
                      <li>
                        • Administrator access to Azure AD (for business
                        accounts)
                      </li>
                      <li>• About 5-10 minutes to complete the setup</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 1: Azure App Registration */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 1: Create Azure App Registration
              </h2>

              <div className="space-y-6">
                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    1.1 Access Azure Portal
                  </h3>
                  <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                    Navigate to the Azure Portal and access App Registrations:
                  </p>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <code className="font-[400] text-[14px] text-[#2A2A2A]">
                        https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps
                      </code>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            "https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps",
                            "azure-url"
                          )
                        }
                        className="flex items-center gap-2 px-3 py-1 bg-[#5E78F4] text-white rounded-[5px] hover:bg-[#4A67E3] transition-colors"
                      >
                        {copiedText === "azure-url" ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                        <span className="text-[12px]">Copy</span>
                      </button>
                    </div>
                  </div>
                  <a
                    href="https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#5E78F4] hover:text-[#F14CE2] transition-colors"
                  >
                    <span className="font-[600] text-[16px]">
                      Open Azure Portal
                    </span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    1.2 Register New Application
                  </h3>
                  <ol className="font-[400] text-[16px] text-[#6B7280] space-y-3">
                    <li>
                      1. Click <strong>"New registration"</strong>
                    </li>
                    <li>
                      2. Enter application name:{" "}
                      <strong>"Email Outreach Platform"</strong>
                    </li>
                    <li>
                      3. Select account types:{" "}
                      <strong>
                        "Accounts in any organizational directory and personal
                        Microsoft accounts"
                      </strong>
                    </li>
                    <li>4. Leave Redirect URI blank for now</li>
                    <li>
                      5. Click <strong>"Register"</strong>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Step 2: Configure Authentication */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 2: Configure Authentication
              </h2>

              <div className="space-y-6">
                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    2.1 Add Redirect URI
                  </h3>
                  <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                    In your app registration, go to{" "}
                    <strong>Authentication</strong> and add this redirect URI:
                  </p>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <code className="font-[400] text-[14px] text-[#2A2A2A]">
                        {window.location.origin}/email/api/auth/outlook/callback
                      </code>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            `${window.location.origin}/email/api/auth/outlook/callback`,
                            "redirect-uri"
                          )
                        }
                        className="flex items-center gap-2 px-3 py-1 bg-[#5E78F4] text-white rounded-[5px] hover:bg-[#4A67E3] transition-colors"
                      >
                        {copiedText === "redirect-uri" ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                        <span className="text-[12px]">Copy</span>
                      </button>
                    </div>
                  </div>
                  <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-[10px] p-4 mb-4">
                    <p className="font-[600] text-[14px] text-[#92400E] mb-2">
                      ⚠️ Critical: Use the EXACT URL shown above in your Azure
                      app registration!
                    </p>
                    <p className="font-[400] text-[12px] text-[#92400E]">
                      If this URL doesn't match exactly what's configured in
                      Azure, you'll get an "invalid_request" error during
                      authentication.
                    </p>
                  </div>

                  <div className="bg-[#FEE2E2] border border-[#DC2626] rounded-[10px] p-4 mb-4">
                    <h4 className="font-[600] text-[14px] text-[#DC2626] mb-2">
                      Troubleshooting "invalid_request" error:
                    </h4>
                    <ul className="font-[400] text-[12px] text-[#DC2626] space-y-1">
                      <li>
                        • Verify the redirect URI in Azure matches exactly:{" "}
                        {window.location.origin}/email/api/auth/outlook/callback
                      </li>
                      <li>
                        • Make sure you selected "Web" platform (not
                        Mobile/Desktop)
                      </li>
                      <li>
                        • Ensure both "Access tokens" and "ID tokens" are
                        enabled
                      </li>
                      <li>
                        • Wait a few minutes for Azure changes to propagate
                      </li>
                    </ul>
                  </div>
                  <ol className="font-[400] text-[16px] text-[#6B7280] space-y-2">
                    <li>
                      1. Click <strong>"Add a platform"</strong>
                    </li>
                    <li>
                      2. Select <strong>"Web"</strong>
                    </li>
                    <li>3. Paste the redirect URI above</li>
                    <li>
                      4. Enable <strong>"Access tokens"</strong> and{" "}
                      <strong>"ID tokens"</strong>
                    </li>
                    <li>
                      5. Click <strong>"Configure"</strong>
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Step 3: API Permissions */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 3: Configure API Permissions
              </h2>

              <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                  3.1 Add Microsoft Graph Permissions
                </h3>
                <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                  Go to <strong>API permissions</strong> and add these delegated
                  permissions:
                </p>
                <div className="bg-[#F8F9FA] rounded-[10px] p-4 mb-4">
                  <ul className="font-[400] text-[14px] text-[#2A2A2A] space-y-2">
                    <li>
                      • <strong>User.Read</strong> - Read user profile
                    </li>
                    <li>
                      • <strong>Mail.Send</strong> - Send mail as user
                    </li>
                    <li>
                      • <strong>Mail.Read</strong> - Read user mail
                    </li>
                  </ul>
                </div>
                <div className="bg-[#FFF3CD] border border-[#FFE69C] rounded-[10px] p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[#856404] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-[600] text-[16px] text-[#856404] mb-1">
                        Important:
                      </p>
                      <p className="font-[400] text-[14px] text-[#856404]">
                        After adding permissions, click{" "}
                        <strong>"Grant admin consent"</strong> if you're an
                        administrator, or ask your admin to approve these
                        permissions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: Get Credentials */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 4: Get Application Credentials
              </h2>

              <div className="space-y-6">
                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    4.1 Application (Client) ID
                  </h3>
                  <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                    From the <strong>Overview</strong> page, copy the{" "}
                    <strong>Application (client) ID</strong>:
                  </p>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-4">
                    <p className="font-[400] text-[14px] text-[#6B7280]">
                      This will be your <strong>MICROSOFT_CLIENT_ID</strong>{" "}
                      environment variable
                    </p>
                  </div>
                </div>

                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    4.2 Client Secret
                  </h3>
                  <ol className="font-[400] text-[16px] text-[#6B7280] space-y-2 mb-4">
                    <li>
                      1. Go to <strong>Certificates & secrets</strong>
                    </li>
                    <li>
                      2. Click <strong>"New client secret"</strong>
                    </li>
                    <li>
                      3. Add description: "Email Outreach Platform Secret"
                    </li>
                    <li>
                      4. Set expiration: <strong>24 months</strong>
                    </li>
                    <li>
                      5. Click <strong>"Add"</strong>
                    </li>
                    <li>
                      6. <strong>Copy the secret value immediately</strong> (it
                      won't be shown again)
                    </li>
                  </ol>
                  <div className="bg-[#F8F9FA] rounded-[10px] p-4">
                    <p className="font-[400] text-[14px] text-[#6B7280]">
                      This will be your <strong>MICROSOFT_CLIENT_SECRET</strong>{" "}
                      environment variable
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5: Add Environment Variables */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 5: Configure Environment Variables
              </h2>

              <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                  Add these environment variables to your Replit project:
                </p>
                <div className="bg-[#F8F9FA] rounded-[10px] p-4 mb-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-[600] text-[14px] text-[#2A2A2A]">
                        MICROSOFT_CLIENT_ID
                      </span>
                      <span className="font-[400] text-[12px] text-[#6B7280]">
                        Your Application (client) ID
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-[600] text-[14px] text-[#2A2A2A]">
                        MICROSOFT_CLIENT_SECRET
                      </span>
                      <span className="font-[400] text-[12px] text-[#6B7280]">
                        Your client secret value
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-[#E3F2FD] border border-[#BBDEFB] rounded-[10px] p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[#1976D2] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-[600] text-[16px] text-[#1976D2] mb-1">
                        Replit Secrets:
                      </p>
                      <p className="font-[400] text-[14px] text-[#1976D2]">
                        Go to your Replit project → Tools → Secrets, and add
                        these environment variables. Never commit these values
                        to your code repository.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6: Test Connection */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Step 6: Test Your Connection
              </h2>

              <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                <p className="font-[400] text-[16px] text-[#6B7280] mb-4">
                  Once you've completed the setup:
                </p>
                <ol className="font-[400] text-[16px] text-[#6B7280] space-y-2 mb-6">
                  <li>1. Restart your Replit application</li>
                  <li>2. Go back to the Email Outreach dashboard</li>
                  <li>
                    3. Click "Configure Email" and select "Connect" for Outlook
                  </li>
                  <li>4. You'll be redirected to Microsoft's login page</li>
                  <li>5. Sign in and grant permissions</li>
                  <li>6. You'll be redirected back with a success message</li>
                </ol>

                <div className="text-center">
                  <Link
                    to="/email-outreach"
                    className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#0078D4] to-[#106EBE] text-white rounded-[15px] font-[600] text-[16px] hover:opacity-90 transition-opacity"
                  >
                    <span>Test Outlook Connection</span>
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Troubleshooting */}
            <div className="mb-12">
              <h2 className="font-[700] text-[24px] text-[#2A2A2A] mb-6">
                Troubleshooting
              </h2>

              <div className="space-y-6">
                <div className="bg-white border border-[#dbdbdb] rounded-[15px] p-6">
                  <h3 className="font-[600] text-[18px] text-[#2A2A2A] mb-4">
                    Common Issues
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <p className="font-[600] text-[16px] text-[#2A2A2A] mb-2">
                        ❌ "Invalid redirect URI"
                      </p>
                      <p className="font-[400] text-[14px] text-[#6B7280]">
                        Ensure the redirect URI in Azure exactly matches your
                        application URL with /email/auth/outlook/callback
                      </p>
                    </div>
                    <div>
                      <p className="font-[600] text-[16px] text-[#2A2A2A] mb-2">
                        ❌ "Insufficient privileges"
                      </p>
                      <p className="font-[400] text-[14px] text-[#6B7280]">
                        Make sure you've added the correct Microsoft Graph
                        permissions and granted admin consent
                      </p>
                    </div>
                    <div>
                      <p className="font-[600] text-[16px] text-[#2A2A2A] mb-2">
                        ❌ "Invalid client secret"
                      </p>
                      <p className="font-[400] text-[14px] text-[#6B7280]">
                        Check that your MICROSOFT_CLIENT_SECRET environment
                        variable matches the secret from Azure
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutlookSetupGuide;
